<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import CertificationInfo from '@components/admin/kyc-certification-info'
import {
  fetchCertificationList,
  rejectCertification,
  approveCertification,
  fetchCertificationTypes,
} from '@apis/admin/kyc'
import fromPairs from 'lodash/fromPairs'
import pick from 'lodash/pick'
import strtotime from 'locutus/php/datetime/strtotime'

export default {
  components: { PageNavigation, CertificationInfo },
  data() {
    return {
      loading: false,
      list: [],
      certification_types: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      s_certification_seq: 0,
      show_dialog: false,
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('kyc_certification_info.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_thisweek'),

            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_week'),

            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('kyc_certification_info.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadCertificationTypes()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchCertificationList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadCertificationTypes() {
      const { data } = await fetchCertificationTypes()

      this.certification_types = data
    },
    async handleReject(seq) {
      try {
        this.loading = true

        if (
          !(await this.$confirm(
            this.$t('admin.msg_denied'),
            this.$t('admin.msg_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await rejectCertification(seq)
        await this.loadList()
        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_denied_success'),
          type: 'success',
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleApprove(seq) {
      try {
        this.loading = true

        if (
          !(await this.$confirm(
            this.$t('admin.msg_approve'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await approveCertification(seq)
        await this.loadList()
        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_approve_success'),
          type: 'success',
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleView(seq) {
      this.s_certification_seq = seq
      this.show_dialog = true
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'type_seq')
      this.search()
    },
    date,
    fromPairs,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t('kyc_certification_info.statistics_exclude_members')
                }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{
                  $t('kyc_certification_info.withdrawal_members_excluded')
                }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.status') }}</div></th
            >
            <td>
              <ElRadioGroup v-model="search_params.status" size="mini">
                <ElRadioButton
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.kyc.certification.status,
                  ]"
                  :key="val[0]"
                  :label="val[0]"
                  >{{ val[1] }}</ElRadioButton
                >
              </ElRadioGroup>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('admin.regdate')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.kyc_cdd') }}</h3>
    <ul :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...certification_types,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.type_seq }"
        :to="{ query: { ...$route.query, type_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>{{ title }}</a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('admin.type') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.status') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.kyc_is_valid') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.close_date') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.kyc_expdate') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
          <th scope="col"><div>&nbsp;</div></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(data, idx) in list"
          :key="data.seq"
          :class="$style[`bg${idx % 2}`]"
        >
          <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
          <template v-if="!$route.params.member_seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{ data.user_id }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.user_name
            }}</td>
            <td :class="[$style.nowrap]">{{ data.email_address }}</td>
          </template>
          <td :class="[$style.wide]">{{ data.type_title }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            fromPairs(constants.kyc.certification.status)[data.status]
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.is_valid === 'Y' ? $t('admin.valid') : $t('admin.invalid')
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.closed_date === null
              ? $t('admin.no_close')
              : date('Y-m-d H:i:s', new Date(data.closed_date))
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.expdate === null
              ? $t('admin.no_appoint')
              : date('Y-m-d H:i:s', new Date(data.expdate))
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            date('Y-m-d H:i:s', new Date(data.regdate))
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">
            <a
              href="#"
              :class="[$style.button]"
              @click.prevent="handleView(data.seq)"
              ><span>{{ $t('kyc.detail') }}</span></a
            >
            <template v-if="data.status === 'PENDING'">
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.blue]"
                @click.prevent="handleApprove(data.seq)"
                ><span>{{ $t('admin.approve') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleReject(data.seq)"
                ><span>{{ $t('admin.denied') }}</span></a
              >
            </template>
          </td>
        </tr>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 7 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 7 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.no_search_cdd') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <ElDialog :visible.sync="show_dialog" :title="$t('admin.kyc_cdd_detail')">
      <CertificationInfo
        v-if="s_certification_seq"
        :key="s_certification_seq"
        :certification_seq="s_certification_seq"
      ></CertificationInfo>
    </ElDialog>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
